import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Button, Divider, DropdownItemProps, Icon, Message, Modal } from "semantic-ui-react";
import {
    compareDates,
    constructDate,
    dateInit,
    deconstructDate,
    getTodayDate,
    riverRangeProps,
} from "../../../models/common";
import {
    CertificateStatus,
    CertificateType,
    certificateTypeOptions,
    ICertificate,
    newCertificateInit,
    statePrintingCertificates,
} from "../../../models/certificate";
import { toast } from "../../..";
import { useParams } from "react-router";
import { FitnessDataForm } from "./FitnessDataForm";
import { CertificateDetailsForm } from "./CertificateDetailsForm";
import { AuthorizationsForm } from "./AuthorizationsForm";
import { DeliveryInfoForm } from "./DeliveryInfoForm";
import { ICrewMember } from "../../../models/crewMember";
import { TranslateProps } from "../../../i18n/TranslateProps";
import { ViewCertificateModal } from "../ViewCertificateModal";
import {
    createNewCertificate,
    renewCertificate,
    updateCertificate,
} from "../../../api/certificates";
import { useSelector } from "react-redux";
import { AppState } from "../../../store/configureStore";
import { UserRole } from "../../../actions/authentificationActions";
import {
    AuthorizationType,
    IAuthorization,
    newAuthorizationInit,
} from "../../../models/authorization";

type Props = {
    open: boolean;
    close: () => void;
    fetchCrewMember: () => Promise<void>;
    dateOfBirth: string;
    certificates: ICertificate[];
    certificate?: ICertificate;
    crewMember: ICrewMember;
    isUpdate?: boolean;
};

const getCertInit = (
    role: string | undefined,
    availableTypes: DropdownItemProps[],
    crewMember: ICrewMember
) => {
    const viadonauValue = availableTypes.some(
        x => x.value === CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS
    )
        ? 64
        : -1;
    return {
        ...newCertificateInit,
        type: role === UserRole.OfficeUserViadonau ? viadonauValue : -1,
        stateOfBirth: crewMember?.idDataSets[0]?.stateOfBirth ?? -1,
    };
};

export const CertificateFormModal = ({
    open,
    close,
    fetchCrewMember,
    dateOfBirth,
    certificates,
    certificate,
    crewMember,
    isUpdate = false,
}: Props) => {
    const user = useSelector((state: AppState) => state.user);
    const intl = useIntl();
    const params: { guid: string } = useParams();
    const [startDate, setStartDate] = useState(
        isUpdate && certificate?.startDate ? deconstructDate(certificate.startDate) : getTodayDate()
    );
    const [endDate, setEndDate] = useState(dateInit);
    const [fitnessStartDate, setFitnessStartDate] = useState(startDate);
    const [fitnessEndDate, setFitnessEndDate] = useState(dateInit);
    const [loading, setLoading] = useState(false);
    const [datesError, setDatesError] = useState(false);
    const [fitnessDatesError, setFitnessDatesError] = useState(false);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [attachment1, setAttachment1] = useState<File>();
    const [attachment2, setAttachment2] = useState<File>();

    const readonly = useMemo(
        () =>
            user.role === UserRole.OfficeUserViadonau &&
            isUpdate &&
            certificate &&
            certificate.type ===
                CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS &&
            !!certificate.dateOfPayment &&
            !!certificate.paymentType &&
            !!certificate.printAcknowledged &&
            !certificate.printFeedback,
        [isUpdate, certificate, user]
    );

    const activeOrSuspendedCertsTypes = certificates
        .filter(
            x =>
                !x.canceled &&
                (x.status === CertificateStatus.Active || x.status === CertificateStatus.Suspended)
        )
        .flatMap(x => x.type)
        .filter(
            x =>
                x !==
                CertificateType.CertificateForSailingOnStretchesOfWaterwaysThatPresentSpecificRisksForNavigationAt
        );
    // "Certificate for sailing on (stretches of) waterways" moze biti duplic
    let availableTypes = TranslateProps(certificateTypeOptions).filter(
        x => !activeOrSuspendedCertsTypes.includes(x.value as number)
    );
    if (!crewMember.cid)
        availableTypes = availableTypes.filter(
            x => (x.value as number) > CertificateType.UcqPassengerNavigationExpert
        );
    if (user.role === UserRole.OfficeUser) {
        availableTypes = availableTypes.filter(
            x => x.value !== CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS
        );
    } else if (user.role === UserRole.OfficeUserViadonau) {
        availableTypes = availableTypes.filter(
            x => x.value === CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS
        );
    }

    const [newCertificate, setNewCertificate] = useState(
        certificate ? certificate : getCertInit(user.role, availableTypes, crewMember)
    );

    const availableRiverRanges = riverRangeProps.filter(
        r =>
            !certificates
                .filter(
                    x =>
                        x.status === CertificateStatus.Active ||
                        x.status === CertificateStatus.Suspended
                )
                .flatMap(x => x.riverKilometers)
                .includes(r.value as number)
    );
    const disabled =
        (newCertificate.type !==
            CertificateType.CertificateForSailingOnStretchesOfWaterwaysThatPresentSpecificRisksForNavigationAt &&
            newCertificate.type !==
                CertificateType.InternationalCertificateForOperatorsOfPleasureCraft &&
            newCertificate.type !==
                CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS &&
            Object.values(endDate).some(x => x === "" || x === -1)) ||
        newCertificate.type < CertificateType.UcqBoatmaster ||
        (statePrintingCertificates.includes(newCertificate.type) &&
            (!newCertificate.nameLineOne ||
                !newCertificate.zipCode ||
                !newCertificate.city ||
                !newCertificate.street ||
                newCertificate.country === -1)) ||
        (newCertificate.type === CertificateType.UcqBoatmaster && newCertificate.gender === 0) ||
        (newCertificate.type === CertificateType.BoatmastersCertificateAt &&
            !newCertificate.vehicleType) ||
        (newCertificate.type > CertificateType.UcqPassengerNavigationExpert &&
            newCertificate.type !==
                CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS &&
            !newCertificate.administrativeNumber) ||
        (newCertificate.type ===
            CertificateType.InternationalCertificateForOperatorsOfPleasureCraft &&
            (newCertificate.vehicleLength < 0 || !newCertificate.validFor)) ||
        datesError ||
        fitnessDatesError ||
        ((newCertificate.type ===
            CertificateType.InternationalCertificateForOperatorsOfPleasureCraft ||
            newCertificate.type ===
                CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS) &&
            crewMember.idDataSets[0].nationality === -1) ||
        (newCertificate.type ===
            CertificateType.CertificateForSailingOnStretchesOfWaterwaysThatPresentSpecificRisksForNavigationAt &&
            newCertificate.riverKilometers.length === 0) ||
        (newCertificate.type ===
            CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS &&
            (newCertificate.stateOfBirth === -1 ||
                !newCertificate.validFor ||
                ((newCertificate.dateOfPayment ||
                    newCertificate.paymentAmount ||
                    newCertificate.paymentType) &&
                    (!newCertificate.dateOfPayment ||
                        !newCertificate.paymentAmount ||
                        !newCertificate.paymentType))));

    const constructDates = () => {
        newCertificate.startDate = constructDate(startDate);
        newCertificate.endDate = constructDate(endDate);
        newCertificate.fitnessIssueDate = constructDate(fitnessStartDate);
        newCertificate.fitnessValidUntil = constructDate(fitnessEndDate);
    };

    const handleSetPreviewModalOpen = async () => {
        constructDates();
        setPreviewModalOpen(true);
    };

    const handleCloseModal = () => {
        setNewCertificate(
            certificate ? certificate : getCertInit(user.role, availableTypes, crewMember)
        );
        close();
        setPreviewModalOpen(false);
    };

    const handleAddCertificate = async () => {
        setLoading(true);
        try {
            constructDates();
            if (newCertificate.type !== CertificateType.UcqBoatmaster)
                newCertificate.restriction96 = "";
            if (
                newCertificate.type === CertificateType.UcqLngExpert ||
                newCertificate.type === CertificateType.UcqPassengerNavigationExpert
            ) {
                newCertificate.limitedAreaDescription = "";
                newCertificate.limitedTaskDescription = "";
                newCertificate.limitedToOneCraftDescription = "";
                newCertificate.restrictions = [];
            }
            if (
                newCertificate.type !== CertificateType.UcqBoatmaster &&
                newCertificate.type !== CertificateType.BoatmastersCertificateAt
            )
                newCertificate.gender = 0;
            newCertificate.crewMemberGuid = crewMember.guid;
            await createNewCertificate(newCertificate, attachment1, attachment2);
            toast(intl.formatMessage({ id: "createdCertificate" }), true);
            fetchCrewMember();
        } catch {
            toast(intl.formatMessage({ id: "unableToCreateCertificate" }), false);
        } finally {
            setLoading(false);
            handleCloseModal();
        }
    };

    const handleUpdateCertificate = async () => {
        setLoading(true);
        try {
            constructDates();
            await updateCertificate(newCertificate, attachment1, attachment2);
            toast(intl.formatMessage({ id: "updatedCertificate" }), true);
            fetchCrewMember();
        } catch {
            toast(intl.formatMessage({ id: "unableToUpdateCertificate" }), false);
        } finally {
            setLoading(false);
            handleCloseModal();
        }
    };

    const handleRenewCertificate = async () => {
        setLoading(true);
        try {
            // merge S-type authorizations if any exist
            if (newCertificate.authorizations.map(a => a.type).includes(AuthorizationType.S)) {
                // get all river kilometers from S-type authorizations
                const strecthes = newCertificate.authorizations
                    .filter(a => a.type === AuthorizationType.S)
                    ?.map(x => x.riverKilometers)
                    .flat()
                    .filter((value, index, array) => array.indexOf(value) === index); // remove duplicate stretches
                // remove S-type authorizations
                newCertificate.authorizations = newCertificate.authorizations.filter(
                    a => a.type !== AuthorizationType.S
                );

                // add new S-type authorization
                const newAuthorization: IAuthorization = {
                    ...newAuthorizationInit,
                    type: AuthorizationType.S,
                    riverKilometers: strecthes,
                    startDate: newCertificate.startDate,
                    endDate: newCertificate.endDate,
                    issuingAuthority: user.authority,
                    status: newCertificate.status,
                    linkedQualificationID: newCertificate.qualificationID,
                    crewMemberGuid: crewMember.guid,
                };
                newCertificate.authorizations.push(newAuthorization);
            }

            constructDates();
            newCertificate.crewMemberGuid = crewMember.guid;
            await renewCertificate(certificate ? certificate.qualificationID : "", newCertificate);
            toast(intl.formatMessage({ id: "certificateRenewed" }), true);
            fetchCrewMember();
        } catch {
            toast(intl.formatMessage({ id: "unableToRenewCertificate" }), false);
        } finally {
            setLoading(false);
            handleCloseModal();
        }
    };

    useEffect(() => {
        setNewCertificate(c => ({
            ...c,
            crewMemberGuid: params.guid,
            issuingAuthority: user.authority,
        }));
    }, [params.guid, user.authority]);

    useEffect(() => {
        if (certificate)
            setNewCertificate(c => ({ ...c, lastQualificationID: certificate.qualificationID }));
        else if (certificates.length > 0 && newCertificate.type >= CertificateType.UcqBoatmaster) {
            const filtered = certificates
                .filter(c => c.type === newCertificate.type)
                .sort(
                    (a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
                );
            if (filtered.length > 0)
                setNewCertificate(c => ({
                    ...c,
                    lastQualificationID: filtered[0].qualificationID,
                }));
            else setNewCertificate(c => ({ ...c, lastQualificationID: "" }));
        } else setNewCertificate(c => ({ ...c, lastQualificationID: "" }));
    }, [certificate, certificates, newCertificate.type]);

    useEffect(() => {
        let comp1 = compareDates(startDate, endDate);
        if (endDate.day === 1 && endDate.month === 1 && endDate.year === 1) comp1 = null;
        const comp2 = compareDates(fitnessStartDate, fitnessEndDate);
        if (
            ((comp1 !== null && !comp1) || (comp2 !== null && !comp2)) &&
            certificate?.type !==
                CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS
        )
            setDatesError(true);
        else setDatesError(false);
        const comp3 = compareDates(endDate, fitnessEndDate, true);
        if (comp3 !== null && !comp3) setFitnessDatesError(true);
        else setFitnessDatesError(false);
    }, [startDate, endDate, fitnessStartDate, fitnessEndDate, certificate]);

    return (
        <>
            <Modal
                open={open}
                className="add-certificate-modal"
                onClose={handleCloseModal}
                dimmer="inverted"
                closeOnDimmerClick={!loading}
                closeOnEscape={!loading}
            >
                <Modal.Header>
                    <span>
                        {intl.formatMessage({
                            id: isUpdate ? "modifyCertificate" : "addCertificate",
                        })}
                    </span>
                    <Icon name="close" link size="large" onClick={handleCloseModal} />
                </Modal.Header>
                <Modal.Content>
                    <CertificateDetailsForm
                        certificate={newCertificate}
                        setCertificate={setNewCertificate}
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                        dateOfBirth={dateOfBirth}
                        setFitnessStartDate={setFitnessStartDate}
                        setFitnessEndDate={setFitnessEndDate}
                        renew={certificate !== undefined}
                        availableCertificatesTypes={availableTypes}
                        availableRiverRanges={availableRiverRanges}
                        isUpdate={isUpdate}
                        setAttachment1={setAttachment1}
                        setAttachment2={setAttachment2}
                        readonly={readonly}
                    />
                    {newCertificate &&
                    statePrintingCertificates &&
                    statePrintingCertificates.includes(newCertificate.type) ? (
                        <>
                            <Divider horizontal>
                                {intl.formatMessage({ id: "deliveryInfo" })}
                            </Divider>
                            <DeliveryInfoForm
                                isUpdate={isUpdate}
                                certificate={newCertificate}
                                setCertificate={setNewCertificate}
                                idDataSet={crewMember.idDataSets[0]}
                                // readonly={readonly}
                            />
                        </>
                    ) : null}
                    {![
                        CertificateType.BoatmastersCertificateAt,
                        CertificateType.InternationalCertificateForOperatorsOfPleasureCraft,
                        CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS,
                        CertificateType.CertificateForSailingOnStretchesOfWaterwaysThatPresentSpecificRisksForNavigationAt,
                        CertificateType.EngineersCertificate,
                    ].includes(newCertificate.type) && ( // fitness data not required for specified types
                        <>
                            <Divider horizontal>
                                {intl.formatMessage({ id: "fitnessData" })}
                            </Divider>
                            <FitnessDataForm
                                certificate={newCertificate}
                                setCertificate={setNewCertificate}
                                fitnessStartDate={fitnessStartDate}
                                setFitnessStartDate={setFitnessStartDate}
                                fitnessEndDate={fitnessEndDate}
                                setFitnessEndDate={setFitnessEndDate}
                            />
                        </>
                    )}
                    {newCertificate.type === CertificateType.UcqBoatmaster && (
                        <>
                            <Divider horizontal>
                                {intl.formatMessage({ id: "specificAuthorizations" })}
                            </Divider>
                            <AuthorizationsForm
                                certificate={newCertificate}
                                setCertificate={setNewCertificate}
                            />
                        </>
                    )}
                    {datesError && (
                        <Message
                            className="error-message"
                            icon="exclamation circle"
                            content={intl.formatMessage({ id: "datesCompareError" })}
                        />
                    )}
                    {fitnessDatesError && (
                        <Message
                            className="error-message"
                            icon="exclamation circle"
                            content={intl.formatMessage({ id: "fitnessDatesCompareError" })}
                        />
                    )}
                    {newCertificate.type ===
                        CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS &&
                        (!crewMember.idDataSets[0].city ||
                            !crewMember.idDataSets[0].country ||
                            crewMember.idDataSets[0].country === -1 ||
                            !crewMember.idDataSets[0].zipCode) && (
                            <Message
                                className="error-message"
                                icon="exclamation circle"
                                content={intl.formatMessage({ id: "missingCrewMemberInfo" })}
                            />
                        )}
                    {(newCertificate.type ===
                        CertificateType.InternationalCertificateForOperatorsOfPleasureCraft ||
                        newCertificate.type ===
                            CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS) &&
                        crewMember.idDataSets[0].nationality === -1 && (
                            <Message
                                className="error-message"
                                icon="exclamation circle"
                                content={intl.formatMessage({ id: "missingCrewMemberNationality" })}
                            />
                        )}
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        primary
                        onClick={handleSetPreviewModalOpen}
                        content={intl.formatMessage({ id: "preview" })}
                        fluid
                        loading={loading}
                        disabled={disabled || loading}
                    />
                </Modal.Actions>
            </Modal>
            <ViewCertificateModal
                open={previewModalOpen}
                close={() => setPreviewModalOpen(false)}
                certificate={newCertificate}
                idDataSet={crewMember.idDataSets[0]}
                renew={certificate !== undefined}
                actions
                handleAddCertificate={handleAddCertificate}
                handleRenewCertificate={handleRenewCertificate}
                handleUpdateCertificate={handleUpdateCertificate}
                isUpdate={isUpdate}
                loading={loading}
                qrBase64={crewMember.qrBase64}
            />
        </>
    );
};
